import React, { useEffect, useState } from "react";
import {
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import {
  IInteractionData,
  interaction,
  reportException,
} from "@origin-digital/reporting-client";
import { errorTracking } from "src/utils/tracking";
import { ProfileFieldMapTypes } from "src/crm/profileFieldMapContextHelpers";
import { toSentenceCase } from "src/utils/helpers";
import { CommsDeliveryPreference } from "../../crm/kraken/graphql-types/globalTypes";

import {
  FieldUpdateHook,
  ContactPreferenceRefetchFieldFunction,
} from "../../crm/profileFieldMap";
import content from "./content.json";
import {
  ContactOptionRecipient,
  ContactOptionType,
} from "./ContactOptionRecipient/ContactOptionRecipient";

export enum ContactPreference {
  EMAIL = "EMAIL",
  POSTAL_MAIL = "POSTAL_MAIL",
}

type ContactPreferenceRecipient = {
  address?: string | null; // billing address for the account, kraken: (account.billingAddress), lpg: (account.billing.postalAddress)
  email?: string | null; // billing email address for the account, kraken: (viewer.email), lpg: (account.billing.email)
  shouldPromptChatOnEmpty: boolean;
};

export type ContactPreferenceProp = {
  id: string | null; // kraken account number
  address?: string; // actual address of property, kraken from property, lpg from service
  supplyTypes: string[]; // Electricity, Natural Gas, LPG - divisionContent
  preference: ContactPreference | null;
  type: ProfileFieldMapTypes;
  recipient: ContactPreferenceRecipient;
};

type SnackbarContent = {
  isDisplayed: boolean;
  message?: string;
  tone?: "positive" | "critical";
};
interface IProps {
  contact: ContactPreferenceProp;
  useUpdate: FieldUpdateHook<ContactPreferenceProp>;
  refetch: ContactPreferenceRefetchFieldFunction;
  type: ProfileFieldMapTypes;
}

export const ContactOption = ({ contact, useUpdate, refetch }: IProps) => {
  const [updateContactPreferences, { success, loading, error }] = useUpdate();
  const [isChanged, setIsChanged] = useState(false);
  const [option, setOption] = useState<ContactPreference | null>(
    contact.preference
  );
  const [snackbar, setSnackbar] = useState<SnackbarContent>({
    isDisplayed: false,
  });

  useEffect(() => {
    if (!loading && isChanged) {
      if (error) {
        setOption(contact.preference);
        setSnackbar({
          isDisplayed: true,
          message: content.error,
          tone: "critical",
        });
        setIsChanged(false);
        errorTracking(error);
        reportException(
          `UpdateContactPreferencesScreen.updateContactPreferences()`,
          error
        );
      } else if (success) {
        setSnackbar({
          isDisplayed: true,
          message: content.success,
          tone: "positive",
        });
        setIsChanged(false);
        refetch(contact.type);
      }
    }
  }, [success, error, contact.preference, refetch, loading, isChanged]);

  useEffect(() => {
    setOption(contact.preference);
  }, [contact.preference]);

  const trackRadioChange = (value: ContactPreference) => {
    const transformedValue =
      value === ContactPreference.POSTAL_MAIL ? "Post" : "Email";
    const interactionData: IInteractionData = {
      appName: "contact-preference",
      id: contact.id || "",
      parentContainer: contact.supplyTypes[0] || "",
      label: transformedValue,
      componentType: "radiobutton",
    };
    interaction("click", interactionData);
  };

  const handleChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    value?: string
  ) => {
    setIsChanged(true);
    setSnackbar((prevState) => ({
      ...prevState,
      isDisplayed: false,
    }));
    const selected =
      value === ContactPreference.EMAIL
        ? ContactPreference.EMAIL
        : ContactPreference.POSTAL_MAIL;

    setOption(selected);
    updateContactPreferences({
      ...contact,
      preference: selected,
    });
    trackRadioChange(selected);
  };

  const getSupplyTypeLabel = (id: string | null, supplyTypes: string[]) => {
    const accountNumber = id?.toUpperCase().trim();
    const numberOfSupplyTypes = supplyTypes.length;
    if (numberOfSupplyTypes === 1) {
      return `${supplyTypes[0]} (${accountNumber})`;
    } else if (numberOfSupplyTypes > 1) {
      const supplyType = toSentenceCase(supplyTypes.join(" and "));
      return `${supplyType} (${accountNumber})`;
    } else {
      return accountNumber;
    }
  };

  return (
    <>
      <Stack
        data-id={`${contact.type?.toLowerCase()}-preferences`}
        space="small"
      >
        <Stack space="xxsmall" id={`contact-account-${contact.id || ""}`}>
          <Text data-id="supply-type" weight="medium" tone="neutralDark">
            {getSupplyTypeLabel(contact.id, contact.supplyTypes)}
          </Text>
          {contact.address && (
            <Text data-id="address">{contact.address.toUpperCase()}</Text>
          )}
        </Stack>
        <Stack space="small">
          <RadioGroup
            onChange={handleChange}
            name="comms-type"
            value={option ?? ""}
            disabled={loading}
            aria-labelledby={`contact-account-${contact.id || ""}`}
          >
            <Radio
              id="email"
              label="Email"
              value={CommsDeliveryPreference.EMAIL}
              disabled={!contact.recipient.email}
              helperText={
                !loading && (
                  <ContactOptionRecipient
                    recipientValue={
                      option === ContactPreference.EMAIL
                        ? contact.recipient.email
                        : null
                    }
                    showPromptChat={
                      !contact.recipient.email &&
                      contact.recipient.shouldPromptChatOnEmpty
                    }
                    contactType={ContactOptionType.EMAIL}
                    profileType={contact.type}
                    refetch={refetch}
                  />
                )
              }
            />
            <Radio
              id="post"
              label="Post"
              value={CommsDeliveryPreference.POSTAL_MAIL}
              disabled={!contact.recipient.address}
              helperText={
                !loading && (
                  <ContactOptionRecipient
                    recipientValue={
                      option === ContactPreference.POSTAL_MAIL
                        ? contact.recipient.address
                        : null
                    }
                    showPromptChat={
                      !contact.recipient.address &&
                      contact.recipient.shouldPromptChatOnEmpty
                    }
                    contactType={ContactOptionType.POST}
                    profileType={contact.type}
                    refetch={refetch}
                  />
                )
              }
            />
          </RadioGroup>
        </Stack>
      </Stack>
      <Snackbar
        data-id="contact-preference-snackbar"
        open={snackbar.isDisplayed}
        tone={snackbar.tone}
        align="center"
        showClose
        onClose={() =>
          setSnackbar((prevState) => ({
            ...prevState,
            isDisplayed: false,
          }))
        }
      >
        {snackbar.message}
      </Snackbar>
    </>
  );
};
